import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
    Col,
    Container,
    Input,
    Badge,
    Button
} from "reactstrap";

const ListContenuRefAdmin = () => {

    const [contents, setContents] = useState([]);
    const [search, setSearch] = useState("");
    const [openUsers, setOpenUsers] = useState({});

    const fetchContents = async () => {
        try {
            const res = await axios.get("/api/content/listContenue", {
                withCredentials: true
            });

            setContents(Array.isArray(res) ? res : []);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchContents();
    }, []);

    // 🔎 Filtrage intelligent
    const filteredContents = useMemo(() => {
        if (!search) return contents;

        const lowerSearch = search.toLowerCase();

        return contents
            .map(user => {
                const filteredProjects = user.projects?.filter(project =>
                    project.name.toLowerCase().includes(lowerSearch) ||
                    project.languages.some(lang =>
                        lang.toLowerCase().includes(lowerSearch)
                    )
                );

                if (
                    user.user.toLowerCase().includes(lowerSearch) ||
                    (filteredProjects && filteredProjects.length > 0)
                ) {
                    return {
                        ...user,
                        projects: filteredProjects?.length > 0 ? filteredProjects : user.projects
                    };
                }

                return null;
            })
            .filter(Boolean);
    }, [search, contents]);

    const toggleUser = (email) => {
        setOpenUsers(prev => ({
            ...prev,
            [email]: !prev[email]
        }));
    };

    const downloadZip = async (url, fileName) => {
        try {
            const response = await axios.get(url, {
                responseType: "blob",
                withCredentials: true
            });

            const objectUrl = window.URL.createObjectURL(new Blob([response]));
            const link = document.createElement("a");
            link.href = objectUrl;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(objectUrl);
        } catch (err) {
            console.error(err);
            alert("Erreur lors du téléchargement");
        }
    };

    // Ne retirer que les caractères réellement interdits dans un nom de fichier Windows,
    // pour garder les lettres accentuées (ex: "Référentiel" reste "Référentiel").
    const safeFileName = (name) => name.replace(/[\\/:*?"<>|\x00-\x1F]/g, "_").trim();

    const handleDownloadProject = (email, projectName) => {
        downloadZip(
            `/api/content/downloadProject/${encodeURIComponent(email)}/${encodeURIComponent(projectName)}`,
            `${safeFileName(projectName)}.zip`
        );
    };

    const handleDownloadLanguage = (email, projectName, language) => {
        downloadZip(
            `/api/content/downloadLanguage/${encodeURIComponent(email)}/${encodeURIComponent(projectName)}/${encodeURIComponent(language)}`,
            `${safeFileName(language)}.zip`
        );
    };

    const handleDeleteProject = async (email, projectName) => {
        if (!window.confirm(`Voulez-vous vraiment supprimer le projet "${projectName}" (toutes les langues) de ${email} ?`)) return;

        try {
            await axios.delete(
                `/api/content/deleteProject/${encodeURIComponent(email)}/${encodeURIComponent(projectName)}`,
                { withCredentials: true }
            );
            fetchContents();
        } catch (err) {
            console.error(err);
            alert("Erreur lors de la suppression du projet");
        }
    };

    const handleDeleteLanguage = async (email, projectName, language) => {
        if (!window.confirm(`Voulez-vous vraiment supprimer la langue "${language}" du projet "${projectName}" de ${email} ?`)) return;

        try {
            await axios.delete(
                `/api/content/deleteLanguage/${encodeURIComponent(email)}/${encodeURIComponent(projectName)}/${encodeURIComponent(language)}`,
                { withCredentials: true }
            );
            fetchContents();
        } catch (err) {
            console.error(err);
            alert("Erreur lors de la suppression de la langue");
        }
    };

    return (
        <div className="page-content">
            <Container fluid>
                <Col lg={12}>
                    <div className="card">
                        <div className="card-body">

                            <h1 className="text-primary text-center mb-4">
                                Liste des Contenus
                            </h1>
                            <h2 className="text-tertiary mb-4 text-center">
                                Liste des contenus référencés par créateur, projet et langue
                            </h2>
                            {/* Information contextuelle selon le rôle */}
                            <p className="text-secondary mb-2 text-center">
                                ℹ️ En tant qu'administrateur, vous pouvez voir tous les contenus référencés par créateur, projet et langue.
                                Utilisez la barre de recherche pour filtrer les résultats.
                                Cliquez sur "Voir projets" pour afficher les projets associés à chaque créateur.
                            </p>

                            {/* 🔎 SEARCH */}
                            <div className="mb-4">
                                <Input
                                    placeholder="Rechercher créateur, projet ou langue..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>

                            {/* 📊 Résumé */}
                            <div className="mb-2 text-muted">
                                {filteredContents.length} créateur(s)
                            </div>

                            {/* 👥 LISTE */}
                            {filteredContents.length > 0 ? (
                                filteredContents.map((userData, userIndex) => {

                                    const email = userData.user;
                                    const username = email.split("@")[0];
                                    const projects = userData.projects || [];

                                    const totalLanguages = projects.reduce(
                                        (acc, p) => acc + (p.languages?.length || 0),
                                        0
                                    );

                                    return (
                                        <div key={userIndex} className="mb-2 border rounded p-2">

                                            {/* USER HEADER */}
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <Badge color="dark" className="me-2">
                                                        {username}
                                                    </Badge>

                                                    <Badge color="secondary" className="me-2">
                                                        {projects.length} référentiel(s)
                                                    </Badge>

                                                    <Badge color="info" className="me-2">
                                                        {totalLanguages} variante(s)
                                                    </Badge>
                                                </div>

                                                <Button
                                                    size="sm"
                                                    color="outline-primary"
                                                    onClick={() => toggleUser(email)}
                                                >
                                                    {openUsers[email] ? "Masquer" : "Voir les référentiels"}
                                                </Button>
                                            </div>

                                            {/* PROJECTS COLLAPSIBLE */}
                                            {openUsers[email] && (
                                                <div className="mt-3">

                                                    {projects.length > 0 ? (
                                                        projects.map((project, projectIndex) => (
                                                            <div
                                                                key={projectIndex}
                                                                className="mb-2 p-2 bg-light rounded"
                                                            >
                                                                <div className="fw-bold mb-1 d-flex justify-content-between align-items-center">
                                                                    <span>📂 {project.name}</span>
                                                                    <span className="d-flex align-items-center gap-1">
                                                                        <button
                                                                            title="Télécharger ce référentiel (zip)"
                                                                            className="btn btn-sm btn-outline-success fs-6 py-0 px-1"
                                                                            onClick={() => handleDownloadProject(email, project.name)}
                                                                        >
                                                                            <i className="mdi mdi-download-outline"></i>
                                                                        </button>
                                                                        <button
                                                                            title="Supprimer ce référentiel"
                                                                            className="btn btn-sm btn-outline-danger fs-6 py-0 px-1"
                                                                            onClick={() => handleDeleteProject(email, project.name)}
                                                                        >
                                                                            <i className="mdi mdi-trash-can-outline"></i>
                                                                        </button>
                                                                    </span>
                                                                </div>

                                                                <div>
                                                                    {project.languages?.map((lang, langIndex) => (
                                                                        <span
                                                                            key={langIndex}
                                                                            className="d-inline-flex align-items-stretch me-1 mb-1"
                                                                        >
                                                                            <Badge
                                                                                color="info"
                                                                                className="d-flex align-items-center"
                                                                                style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                                                                            >
                                                                                {lang}
                                                                            </Badge>
                                                                            <button
                                                                                title="Télécharger cette variante (zip)"
                                                                                className="btn btn-outline-success d-flex align-items-center py-0 px-1"
                                                                                style={{
                                                                                    borderRadius: 0,
                                                                                    fontSize: "0.9rem",
                                                                                    lineHeight: 1
                                                                                }}
                                                                                onClick={() => handleDownloadLanguage(email, project.name, lang)}
                                                                            >
                                                                                <i className="mdi mdi-download-outline"></i>
                                                                            </button>
                                                                            <button
                                                                                title="Supprimer cette variante"
                                                                                className="btn btn-outline-danger d-flex align-items-center py-0 px-1"
                                                                                style={{
                                                                                    borderTopLeftRadius: 0,
                                                                                    borderBottomLeftRadius: 0,
                                                                                    fontSize: "0.9rem",
                                                                                    lineHeight: 1
                                                                                }}
                                                                                onClick={() => handleDeleteLanguage(email, project.name, lang)}
                                                                            >
                                                                                <i className="mdi mdi-trash-can-outline"></i>
                                                                            </button>
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div className="text-muted">
                                                            Aucun projet
                                                        </div>
                                                    )}

                                                </div>
                                            )}

                                        </div>
                                    );
                                })
                            ) : (
                                <div className="text-center text-muted mt-4">
                                    Aucun contenu trouvé
                                </div>
                            )}

                        </div>
                    </div>
                </Col>
            </Container>
        </div>
    );
};

export default ListContenuRefAdmin;

//Creators : Florian Cohen-Joly  @3DDGAMES -- Under license
